const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', border: 'var(--border)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)',
};
const AF = {
  h: "'Space Grotesk', system-ui, sans-serif",
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

function LegalNav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: C.bg, borderBottom: `1px solid ${C.border}`,
      height: 64, display: 'flex', alignItems: 'center',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', textDecoration: 'none' }}>
          Conjugr<span style={{ color: C.accent }}>8</span>
        </a>
        <a href="/learn-spanish" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>
          Start learning →
        </a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${C.border}`, padding: '32px 24px', marginTop: 80 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>© 2026 Conjugr8. All rights reserved.</span>
        <div style={{ display: 'flex', gap: 24 }}>
          <a href="/privacy" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Terms</a>
          <a href="/contact" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Contact</a>
        </div>
      </div>
    </footer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: AF.h, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 12, letterSpacing: '-0.3px' }}>{title}</h2>
      <div style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, lineHeight: 1.75 }}>{children}</div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
      <LegalNav />
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px 0' }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: C.accent, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16 }}>
            Legal
          </div>
          <h1 style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 700, color: C.text, letterSpacing: '-1px', marginBottom: 12 }}>
            Terms of Service
          </h1>
          <p style={{ fontFamily: AF.b, fontSize: 14, color: C.dim }}>
            Effective date: 15 June 2026 · Last updated: 15 June 2026
          </p>
        </div>

        <Section title="Agreement to these terms">
          <p>By accessing or using Conjugr8 at conjugr8.com ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.</p>
          <p style={{ marginTop: 12 }}>Conjugr8 is operated by Ashton Spina ("we", "us", "our").</p>
        </Section>

        <Section title="The Service">
          <p>Conjugr8 is a web-based language-learning application that provides verb conjugation practice, vocabulary flashcards, sentence building exercises, comprehension reading, and related learning tools for Spanish, Portuguese, and Finnish.</p>
          <p style={{ marginTop: 12 }}>We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.</p>
        </Section>

        <Section title="Your account">
          <p>You may use core features of Conjugr8 without an account. To sync progress across devices or access certain features, you must sign in using a supported OAuth provider (currently Google via Auth0).</p>
          <p style={{ marginTop: 12 }}>You are responsible for maintaining the security of your account and for all activity that occurs under your account. You must notify us immediately at <a href="mailto:jobcrawls@gmail.com" style={{ color: C.accent }}>jobcrawls@gmail.com</a> of any unauthorised use.</p>
          <p style={{ marginTop: 12 }}>You must be at least 13 years old (or 16 in the EEA) to use the Service. By using the Service, you represent that you meet this age requirement.</p>
        </Section>

        <Section title="Acceptable use">
          <p>You agree not to:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Use the Service for any unlawful purpose or in violation of any applicable law</li>
            <li>Attempt to gain unauthorised access to any part of the Service or its infrastructure</li>
            <li>Scrape, crawl, or extract data from the Service in an automated way without our written permission</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Upload or transmit malicious code or interfere with the Service's integrity</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
          </ul>
        </Section>

        <Section title="Intellectual property">
          <p>The Service and its original content (excluding user-provided content and third-party data such as vocabulary data) are and remain our exclusive property. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.</p>
          <p style={{ marginTop: 12 }}>Vocabulary and example sentence data is sourced from open datasets and third-party providers. AI-generated content is produced by OpenAI's models and is subject to OpenAI's usage policies.</p>
        </Section>

        <Section title="Subscription and payments">
          <p>Conjugr8 currently offers a free tier. Paid subscription plans may be introduced in the future. If and when paid features are offered, additional terms (including pricing, billing cycle, cancellation, and refund policy) will be provided at the point of purchase and incorporated into these Terms.</p>
        </Section>

        <Section title="Disclaimer of warranties">
          <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components.</p>
          <p style={{ marginTop: 12 }}>We do not guarantee the accuracy of AI-generated sentences, vocabulary translations, or any other educational content. Always verify important language information with a qualified teacher or authoritative source.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>To the fullest extent permitted by applicable law, Conjugr8 and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, loss of revenue, or loss of goodwill, arising out of or in connection with your use of the Service, even if we have been advised of the possibility of such damages.</p>
          <p style={{ marginTop: 12 }}>Our total liability to you for any claim arising from or related to the Service shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim or (b) £50 GBP.</p>
        </Section>

        <Section title="Governing law">
          <p>These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales, except that if you are a consumer in the EEA, you may also bring proceedings in your country of residence.</p>
        </Section>

        <Section title="Changes to these terms">
          <p>We may update these Terms from time to time. We will provide at least 14 days' notice of material changes by updating the "Last updated" date. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about these Terms? Email us at <a href="mailto:jobcrawls@gmail.com" style={{ color: C.accent }}>jobcrawls@gmail.com</a>.</p>
        </Section>
      </main>
      <LegalFooter />
    </div>
  );
}
